import { NavLink, useParams } from "react-router-dom";
import "./top-navigation.scss";
import { LeftIcon, PlayIcon, SettingIcon, ShareIcon } from "@components/icons";
import { Button } from "@components/button";
import { FC, useCallback, useMemo, useState } from "react";
import { CustomTooltip } from "@components/tool-tip/";
import { Permission } from "@contexts/permissions.context";
import { AppAndFormAction, PermissionTypes } from "@archetypes/gql";
import { UPDATE_FORM_STATUS } from "@queries/index";
import { useMutation } from "@apollo/client";
import ShareFormDialog from "@dialogs/share-form-dialog";
import { FormSettingDialog } from "@dialogs/form-settings";
interface TopNavigationInterface {
  formName?: string;
  formDescription?: string;
  actions?: AppAndFormAction[];
}

export const TopNavigation: FC<TopNavigationInterface> = ({
  formName,
  formDescription,
  actions,
}) => {
  const { appId, formId } = useParams();
  const [isShareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [isSettingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const baseRoute = useMemo(() => {
    return "/builder/apps/" + appId + "/forms/" + formId;
  }, [appId, formId]);
  const [updateFormStatus, { loading: loadingFormStatus }] = useMutation(UPDATE_FORM_STATUS);

  const triggerFormStatusUpdate = useCallback(
    (status: AppAndFormAction) => {
      updateFormStatus({
        variables: {
          input: {
            formId: formId,
            currentStatus: status + "ED",
          },
        },
      });
    },
    [updateFormStatus, formId],
  );

  const { isPublishAllowed, isUnPublishAllowed } = useMemo(() => {
    return {
      isPublishAllowed: actions?.includes(AppAndFormAction.PUBLISH),
      isUnPublishAllowed: actions?.includes(AppAndFormAction.UNPUBLISH),
    };
  }, [actions]);

  return (
    <nav className="top-menu-container">
      <div className="logo-area">
        <NavLink to={"/builder/apps/" + appId + "/forms"}>
          <LeftIcon />
        </NavLink>
      </div>
      <div className="title-description-area">
        <h3>{formName}</h3>
        <p>{formDescription}</p>
      </div>
      <ul className="top-menu">
        <li>
          <NavLink to={baseRoute + "/design"}>Design</NavLink>
        </li>
        <Permission access={[PermissionTypes.RESPONSE_TEMPLATE_READ]}>
          <li>
            <NavLink to={baseRoute + "/templates"}>Data View</NavLink>
          </li>
          <li>
            <NavLink to={baseRoute + "/layouts"}>Layouts</NavLink>
          </li>
        </Permission>
        <Permission access={[PermissionTypes.RULE_READ]}>
          <li>
            <NavLink to={baseRoute + "/rules"}>Rules</NavLink>
          </li>
        </Permission>
        <li>
          <NavLink to={baseRoute + "/responses"}>Responses</NavLink>
        </li>
        <Permission access={[PermissionTypes.DASHBOARD_READ]}>
          <li>
            <NavLink to={baseRoute + "/widgets"}>Widgets</NavLink>
          </li>
        </Permission>
      </ul>
      <ul className="top-menu w-auto device-view">
        {isPublishAllowed && (
          <li className="seperator">
            <Button
              onClick={() => {
                setSettingsModalOpen(true);
              }}
            >
              <CustomTooltip content="Settings">
                <SettingIcon />
              </CustomTooltip>
            </Button>
          </li>
        )}
        <li className="seperator">
          <Button
            onClick={() => {
              setShareModalOpen(true);
            }}
          >
            <CustomTooltip content="Share">
              <ShareIcon />
            </CustomTooltip>
          </Button>
        </li>
        <li className="seperator">
          <NavLink to={baseRoute + "/preview"}>
            <CustomTooltip content="Preview">
              <PlayIcon />
            </CustomTooltip>
          </NavLink>
        </li>
        <li>
          {isUnPublishAllowed ? (
            <>
              <Button
                loading={loadingFormStatus}
                onClick={() => triggerFormStatusUpdate(AppAndFormAction.UNPUBLISH)}
                variant="default-bordered"
                loaderHeight={24}
                loaderColor="#00033e"
              >
                Unpublish
              </Button>
            </>
          ) : null}
          {isPublishAllowed ? (
            <Button
              variant="primary"
              loading={loadingFormStatus}
              onClick={() => triggerFormStatusUpdate(AppAndFormAction.PUBLISH)}
              loaderHeight={24}
            >
              Publish
            </Button>
          ) : null}
        </li>
      </ul>
      <FormSettingDialog
        isOpen={isSettingsModalOpen}
        onClose={() => {
          setSettingsModalOpen(false);
        }}
      />
      <ShareFormDialog
        appId={appId}
        formId={formId}
        isOpen={isShareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
        }}
      />
    </nav>
  );
};
